import { Navigate, useSearchParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useOptionalAuth } from '../hooks/useOptionalAuth';

// I7 (hardening): every `localStorage` call in this file was unguarded.
// Mirrors hooks/useTheme.tsx's readStoredTheme/writeStoredTheme — a throw
// (private-browsing / locked-down webview storage restrictions) degrades to
// "no stored realm" / "didn't persist" rather than crashing the login
// screen, which is exactly the one screen a user with broken storage most
// needs to still render.
function readStoredRealm(): string | null {
  try {
    return localStorage.getItem('billing_realm');
  } catch {
    return null;
  }
}

function writeStoredRealm(realm: string): void {
  try {
    localStorage.setItem('billing_realm', realm);
  } catch {
    // Storage unavailable or full — the realm still works for this session
    // via React state, it just won't persist across reloads/redirects.
  }
}

function clearStoredRealm(): void {
  try {
    localStorage.removeItem('billing_realm');
  } catch {
    // Nothing to do if storage is inaccessible — treat as already cleared.
  }
}

export default function Login() {
  const auth = useOptionalAuth();
  const [searchParams] = useSearchParams();
  const [realm, setRealm] = useState(() => {
    return searchParams.get('realm') || readStoredRealm() || '';
  });
  const [error, setError] = useState<string | null>(null);

  // If coming from Portal with realm param, store it
  useEffect(() => {
    const realmParam = searchParams.get('realm');
    if (realmParam) {
      writeStoredRealm(realmParam);
      setRealm(realmParam);
    }
  }, [searchParams]);

  // Auto-trigger the Keycloak redirect when the realm is already known so users
  // arriving from the upstream tile picker don't need to click "Sign in with
  // Keycloak" — their SSO cookie makes this hop silent.
  const [autoRedirecting, setAutoRedirecting] = useState(false);
  useEffect(() => {
    if (
      auth &&
      !auth.isAuthenticated &&
      !auth.isLoading &&
      !auth.error &&
      !autoRedirecting &&
      realm.trim().length > 0
    ) {
      setAutoRedirecting(true);
      auth.signinRedirect();
    }
  }, [auth, realm, autoRedirecting]);

  // If authenticated, redirect to dashboard
  if (auth?.isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Show a spinner while the auto-redirect is in flight (or while OIDC is loading
  // an existing session) — avoids a flash of the manual login form.
  if (autoRedirecting || (auth?.isLoading && realm.trim().length > 0)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800 p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white/80">Signing in…</p>
        </div>
      </div>
    );
  }

  const handleLogin = () => {
    if (!realm.trim()) {
      setError('Please enter a realm');
      return;
    }

    writeStoredRealm(realm.trim());

    // Redirect to login - need to reinitialize auth with new realm
    // Reload the page to pick up the new realm in OIDC config.
    // No `?realm=` query param: it's redundant (main.tsx's getRealmFromUrl
    // falls back to the localStorage value set above on this full-page
    // navigation) and would otherwise interpolate unencoded user input into
    // the URL.
    window.location.href = `${window.location.origin}/login`;
  };

  const handleOIDCLogin = () => {
    if (!realm.trim()) {
      setError('Please enter a realm');
      return;
    }
    writeStoredRealm(realm.trim());

    // If auth is available, use OIDC redirect
    if (auth) {
      auth.signinRedirect();
    } else {
      // No auth provider yet - reload to initialize with realm.
      // Same reasoning as handleLogin above: localStorage already has the
      // realm, so the query param is redundant and skipped.
      window.location.href = `${window.location.origin}/login`;
    }
  };

  // If realm is set and we have auth provider, show login button
  const storedRealm = readStoredRealm();
  const hasRealm = storedRealm && storedRealm.trim().length > 0 && auth !== null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800 p-4">
      <div className="w-full max-w-md">
        {/* Kept as a plain bg-surface-raised div (not <Card>) rather than
            Card, matching the group A/D precedent for one-off floating/modal
            containers (TariffManagement's Create Tariff modal, Pockets'
            Create Pocket modal, Tenants' Add/Edit modal) — Card's own
            rounded-lg would silently override this card's intentional
            rounded-2xl. */}
        <div className="bg-surface-raised rounded-2xl shadow-2xl p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            {/* bg-primary-100 is a literal Tailwind colour (not a CSS-var
                token) so it doesn't auto-switch with the theme — without
                dark:bg-primary-900 the icon (text-accent, which resolves to
                teal-400 in dark) would sit on a background that stays
                teal-100, near-invisible (contrastRatio('#2dd4bf','#ccfbf1')
                = 1.65:1). dark:bg-primary-900 (#134e4a, the same hex as
                Badge's success-tone dark fill) against teal-400 text is
                5.09:1 — computed by hand, passes AA. */}
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-100 dark:bg-primary-900 mb-4">
              <svg className="w-8 h-8 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="3" y1="9" x2="21" y2="9"></line>
                <line x1="9" y1="21" x2="9" y2="9"></line>
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-content">UtilSync Billing</h1>
            <p className="text-content-muted mt-2">Sign in to manage bills and tariffs</p>
          </div>

          {/* Realm selection */}
          {!hasRealm ? (
            <div className="space-y-4">
              <div>
                <label htmlFor="realm" className="block text-sm font-medium text-content mb-1">
                  Realm
                </label>
                <input
                  type="text"
                  id="realm"
                  value={realm}
                  onChange={(e) => {
                    setRealm(e.target.value);
                    setError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleLogin();
                    }
                  }}
                  placeholder="e.g., client123"
                  className="w-full px-4 py-3 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                />
                {/* text-red-600 is 3.14:1 against surface-raised dark
                    (#292524) — fails AA. dark:text-red-400 is 5.49:1,
                    computed by hand — passes. */}
                {error && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>}
              </div>
              <button
                onClick={handleLogin}
                className="w-full py-3 px-4 bg-accent text-accent-fg font-medium rounded-lg hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
              >
                Continue
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-surface-sunken rounded-lg">
                <p className="text-sm text-content-muted">
                  Signing in to realm: <span className="font-semibold text-content">{storedRealm}</span>
                </p>
                <button
                  onClick={() => {
                    clearStoredRealm();
                    setRealm('');
                  }}
                  className="text-sm text-accent hover:opacity-90 mt-1"
                >
                  Change realm
                </button>
              </div>
              <button
                onClick={handleOIDCLogin}
                disabled={auth?.isLoading}
                className="w-full py-3 px-4 bg-accent text-accent-fg font-medium rounded-lg hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {auth?.isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  'Sign in'
                )}
              </button>
            </div>
          )}

          {/* Footer */}
          <div className="mt-8 text-center text-sm text-content-muted">
            <p>Part of the UtilSync IoT Platform</p>
          </div>
        </div>
      </div>
    </div>
  );
}
